import {
  FVErrorCode,
  PreviewType,
  SessionEventType,
} from "@dropbox/file-viewer";
import { getFileTypeFromString } from "@mirage/analytics/events/base/file_type-utils";
import { PAP_Preview_File } from "@mirage/analytics/events/types/preview_file";

import type { CoreSession } from "@dropbox/file-viewer/core/logging/session";
import type { Logger } from "@dropbox/file-viewer/core/middlewares/analytics_types";
import type { ReportPapEventFn } from "@mirage/analytics/AnalyticsProvider";
import type { PAPEventState } from "@mirage/analytics/events/base/event";
import type { PreviewFailureReason } from "@mirage/analytics/events/enums/preview_failure_reason";
import type { PreviewType as PAP_PreviewType } from "@mirage/analytics/events/enums/preview_type";

export type PreviewLoggingFile = {
  extension: string;
  file_id: string;
  previewType?: PreviewType;
  size: number;
};

export const getFileViewerLogger = (
  reportPapEvent: ReportPapEventFn,
): Logger => ({
  log: (
    eventName: string,
    session: CoreSession<Record<string, string>>,
    _tags?: Record<string, string>,
  ) => {
    if (eventName === "user_action") {
      // There are PAP events for File Viewer user actions, but we haven't yet ported them to Dash.
      // It's not currently possible to perform any substantive user actions in File Viewer from Dash.
      return;
    }

    const file: PreviewLoggingFile = {
      extension: session.file_extension ?? "",
      previewType: session.extra.preview_type as PreviewType | undefined,
      file_id: session.file_id,
      size: session.file_preview_size ?? 0,
    };
    logPreviewToPap(reportPapEvent, eventName, file, session);
  },
  logDuration: (_args: {
    eventName: string;
    durationInMs: number;
    session?: CoreSession<Record<string, string>>;
    tags?: Record<string, string>;
    shouldLogToHive?: boolean;
  }) => {
    // There are no PAP types for File Viewer's duration-based events. Do nothing.
  },
});

/**
 * Maps stringly typed previewType to PAP's previewType
 */
function getPapPreviewType(previewType?: string): PAP_PreviewType | undefined {
  // Unmatched PAP_PreviewType enum:
  // link_file
  // external_doc
  // external_preview
  // no_preview
  // folder
  switch (previewType) {
    case PreviewType.Image:
    case PreviewType.Doc:
    case PreviewType.Video:
    case PreviewType.Audio:
    case PreviewType.Archive:
    case PreviewType.CloudDoc:
    case PreviewType.Linkfile:
    case PreviewType.Error:
    case PreviewType.Excel:
    case PreviewType.Text:
      return previewType;
    case PreviewType.SsrDoc:
      return "doc";
    case PreviewType.RawHTML:
      return "html";
    case PreviewType.Photo:
      return "image";
    default:
      return undefined;
  }
}

const logPreviewToPap = async (
  logPapEvent: ReportPapEventFn,
  eventName: string,
  file: PreviewLoggingFile,
  session: CoreSession<Record<string, string>>,
) => {
  const fileExtension = file.extension;

  let eventState: PAPEventState;
  let previewFailureReason: PreviewFailureReason | undefined = undefined;
  const errorCode = session.extra.error_code;

  switch (eventName) {
    case SessionEventType.FilePreviewAttemptStarted:
      eventState = "start";
      break;
    case SessionEventType.FilePreviewRenderSucceeded:
      eventState = "success";
      break;
    case SessionEventType.FilePreviewSupportDenied:
      eventState = "rejected";
      previewFailureReason = "unimplemented";
      break;
    case SessionEventType.FilePreviewDownloadFailed:
      eventState = "failed";
      previewFailureReason = "network_error";
      break;
    case SessionEventType.FilePreviewRenderFailed:
      eventState =
        errorCode === FVErrorCode.PasswordProtected ||
        errorCode === FVErrorCode.NestedFiletypeNotSupported
          ? "rejected"
          : "failed";
      previewFailureReason = "rendering_error";
      break;
    case SessionEventType.FilePreviewSessionEnded:
      switch (session.extra.last_event) {
        case SessionEventType.FilePreviewSupportDenied:
        case SessionEventType.FilePreviewDownloadFailed:
        case SessionEventType.FilePreviewRenderFailed:
        case SessionEventType.FilePreviewRenderSucceeded:
          return;
        default:
          eventState = "canceled";
      }
      break;
    default:
      return;
  }

  const timeSinceStartMs = session.extra.page_start_ts
    ? Date.now() - parseInt(session.extra.page_start_ts, 10)
    : undefined;

  logPapEvent(
    PAP_Preview_File({
      eventState: eventState,
      actionScreen: "dash",
      fileType: getFileTypeFromString(fileExtension),
      fileSize: file.size,
      previewType: file.previewType
        ? getPapPreviewType(file.previewType)
        : undefined,
      startType: "initial",
      actionId: session.file_preview_session_id,
      contentIdentifier: file.file_id,
      previewFailureReason,
      timeSinceStartMs,
      validationTraceId: session.extra.validation_trace_id,
    }),
  );
};
